import {useInfiniteQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../constants'

const PAGE_SIZE = 12
const SCAPI_BATCH_LIMIT = 24

const generateBatches = (arr, size) => {
    const out = []
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
    return out
}

export default function usePaginatedProducts({
    productIdArray,
    activeCategory,
    api,
    getTokenWhenReady
}) {
    const queryKey = useMemo(() => productIdArray?.join('|'), [productIdArray])

    const query = useInfiniteQuery({
        queryKey: ['products-colorbar-plp', activeCategory?.id, queryKey],
        enabled: !!activeCategory && productIdArray.length > 0,
        initialPageParam: 0,

        getNextPageParam: (lastPage, allPages) => {
            const nextStart = allPages.length * PAGE_SIZE
            return nextStart < productIdArray.length ? allPages.length : undefined
        },

        queryFn: async ({pageParam = 0}) => {
            const start = pageParam * PAGE_SIZE
            const pageIds = productIdArray.slice(start, start + PAGE_SIZE)

            if (pageIds.length === 0) {
                return []
            }

            const token = await getTokenWhenReady()
            const batches = generateBatches(pageIds, SCAPI_BATCH_LIMIT)

            const responses = await Promise.all(
                batches.map((ids) =>
                    api.shopperProducts.getProducts({
                        parameters: {
                            ids: ids.join(','),
                            allImages: true,
                            perPricebook: true,
                            select: SELECTED_ATTRIBUTES_WIDGET_PRODUCTS,
                            expand: [
                                'availability',
                                'links',
                                'promotions',
                                'options',
                                'images',
                                'prices',
                                'variations'
                            ]
                        },
                        headers: {Authorization: `Bearer ${token}`}
                    })
                )
            )
            return responses.flatMap((res) => res?.data || [])
        }
    })
    const products = useMemo(() => {
        return query.data?.pages?.flat() || []
    }, [query.data])

    return {
        products,
        fetchMore: query.fetchNextPage,
        hasMore: query.hasNextPage,
        isFetchingMore: query.isFetchingNextPage,
        ...query
    }
}
